import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Upload, Play, Pause, Trash2, X, Music,
  FolderOpen, Search, Grid, List, ChevronDown, ChevronRight,
  Package, FileAudio, CheckCircle, Loader2, Move, RefreshCw
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminKitSamples = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, isEditor, getAuthHeader, loading: authLoading } = useAuth();
  
  // State
  const [samples, setSamples] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKit, setSelectedKit] = useState('all');
  const [playingSample, setPlayingSample] = useState(null);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, unassigned: 0, assigned: 0, by_kit: [] });
  const [expandedKits, setExpandedKits] = useState({});
  
  const audioRef = useRef(new Audio());
  const folderInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check auth on mount - wait for auth loading to complete
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || (!isAdmin() && !isEditor()))) {
      toast.error('Please log in as admin to access this page');
      navigate('/admin');
    }
  }, [isAuthenticated, isAdmin, isEditor, navigate, authLoading]);

  // Fetch data when auth is ready
  useEffect(() => {
    if (!authLoading && isAuthenticated && (isAdmin() || isEditor())) {
      fetchData();
    }
  }, [authLoading, isAuthenticated]);

  // Cleanup audio on unmount
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const headers = getAuthHeader();
    
    console.log('Fetching data with headers:', headers);
    
    if (!headers.Authorization) {
      console.error('No auth header available');
      toast.error('Not authenticated. Please log in.');
      navigate('/admin');
      return;
    }
    
    try {
      const [samplesRes, packagesRes, statsRes] = await Promise.all([
        fetch(`${API}/api/kit-samples`, { headers }),
        fetch(`${API}/api/instrument-packages`, { headers }),
        fetch(`${API}/api/kit-samples/stats`, { headers })
      ]);

      console.log('API responses:', samplesRes.status, packagesRes.status, statsRes.status);

      if (samplesRes.ok) {
        const data = await samplesRes.json();
        setSamples(data.samples || []);
      } else if (samplesRes.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/admin');
        return;
      }
      
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        setPackages(data.packages || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle folder upload (webkitdirectory)
  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Get auth header from context
    const headers = getAuthHeader();
    if (!headers.Authorization) {
      toast.error('Not authenticated. Please log in.');
      navigate('/admin');
      return;
    }
    
    console.log('Starting folder upload with auth header');

    // Group files by folder
    const filesByFolder = {};
    files.forEach(file => {
      const pathParts = file.webkitRelativePath.split('/');
      const folderName = pathParts.length > 1 ? pathParts[0] : 'root';
      if (!filesByFolder[folderName]) {
        filesByFolder[folderName] = [];
      }
      // Only add audio files
      if (file.type.startsWith('audio/') || file.name.match(/\.(wav|mp3|ogg|flac|aiff|aif)$/i)) {
        filesByFolder[folderName].push(file);
      }
    });

    const folders = Object.keys(filesByFolder);
    const totalFiles = Object.values(filesByFolder).flat().length;
    
    if (totalFiles === 0) {
      toast.error('No audio files found in selected folder(s)');
      return;
    }

    toast.info(`Found ${totalFiles} audio files in ${folders.length} folder(s)`);
    
    setUploading(true);
    setUploadProgress({ current: 0, total: totalFiles });
    
    let uploadedCount = 0;
    let errorCount = 0;

    for (const folderName of folders) {
      const folderFiles = filesByFolder[folderName];
      
      // Upload files in batches of 5
      for (let i = 0; i < folderFiles.length; i += 5) {
        const batch = folderFiles.slice(i, i + 5);
        const formData = new FormData();
        
        batch.forEach(file => {
          formData.append('files', file);
        });
        formData.append('folder_name', folderName);

        try {
          console.log(`Uploading batch to folder: ${folderName}, files: ${batch.map(f => f.name).join(', ')}`);
          
          const res = await fetch(`${API}/api/kit-samples/upload-batch`, {
            method: 'POST',
            headers: headers,
            body: formData
          });

          console.log(`Upload response: ${res.status}`);

          if (res.ok) {
            const data = await res.json();
            uploadedCount += data.uploaded;
            errorCount += data.errors;
            console.log(`Batch uploaded: ${data.uploaded} success, ${data.errors} errors`);
          } else {
            const errorText = await res.text();
            console.error(`Upload failed: ${res.status} - ${errorText}`);
            errorCount += batch.length;
          }
        } catch (err) {
          console.error('Upload error:', err);
          errorCount += batch.length;
        }

        setUploadProgress(prev => ({ ...prev, current: prev.current + batch.length }));
      }
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    if (uploadedCount > 0) {
      toast.success(`Uploaded ${uploadedCount} samples`);
      fetchData();
    }
    if (errorCount > 0) {
      toast.warning(`${errorCount} files failed to upload`);
    }

    // Reset input
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  // Handle single/multiple file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const audioFiles = files.filter(f => 
      f.type.startsWith('audio/') || f.name.match(/\.(wav|mp3|ogg|flac|aiff|aif)$/i)
    );

    if (audioFiles.length === 0) {
      toast.error('No audio files selected');
      return;
    }

    const headers = getAuthHeader();
    if (!headers.Authorization) {
      toast.error('Not authenticated. Please log in.');
      navigate('/admin');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: audioFiles.length });

    const formData = new FormData();
    audioFiles.forEach(file => formData.append('files', file));

    try {
      const res = await fetch(`${API}/api/kit-samples/upload-batch`, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Uploaded ${data.uploaded} samples`);
        fetchData();
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      toast.error('Upload error');
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Play/pause sample
  const togglePlay = (sample) => {
    const audio = audioRef.current;
    
    if (playingSample === sample.id) {
      audio.pause();
      setPlayingSample(null);
    } else {
      audio.src = `${API}${sample.url}`;
      audio.play().catch(() => {});
      setPlayingSample(sample.id);
      
      audio.onended = () => setPlayingSample(null);
    }
  };

  // Delete sample
  const handleDelete = async (sampleId) => {
    if (!window.confirm('Delete this sample?')) return;

    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${API}/api/kit-samples/${sampleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Sample deleted');
        setSamples(prev => prev.filter(s => s.id !== sampleId));
        setSelectedSamples(prev => prev.filter(id => id !== sampleId));
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Assign samples to package
  const handleAssign = async (packageId, subInstrumentId = null) => {
    if (selectedSamples.length === 0) return;

    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    selectedSamples.forEach(id => formData.append('sample_ids', id));
    formData.append('package_id', packageId);
    if (subInstrumentId) {
      formData.append('sub_instrument_id', subInstrumentId);
    }

    try {
      const res = await fetch(`${API}/api/kit-samples/assign-to-package`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Assigned ${data.assigned_count} samples to ${data.package_name}`);
        setSelectedSamples([]);
        setShowAssignModal(false);
        fetchData();
      }
    } catch (err) {
      toast.error('Assignment failed');
    }
  };

  // Toggle sample selection
  const toggleSelectSample = (sampleId) => {
    setSelectedSamples(prev => 
      prev.includes(sampleId) 
        ? prev.filter(id => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  // Select all visible samples
  const selectAllVisible = () => {
    const visibleIds = filteredSamples.map(s => s.id);
    setSelectedSamples(visibleIds);
  };

  // Filter samples
  const filteredSamples = samples.filter(s => {
    const matchesSearch = !searchQuery || 
      s.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.instrument_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesKit = selectedKit === 'all' || 
      (selectedKit === 'unassigned' && !s.kit_id) ||
      s.kit_id === selectedKit;
    
    return matchesSearch && matchesKit;
  });

  // Group samples by kit for organized view
  const samplesByKit = filteredSamples.reduce((acc, sample) => {
    const kitId = sample.kit_id || 'unassigned';
    if (!acc[kitId]) acc[kitId] = [];
    acc[kitId].push(sample);
    return acc;
  }, {});

  // Get kit name by ID
  const getKitName = (kitId) => {
    if (!kitId || kitId === 'unassigned') return 'Unassigned';
    const pkg = packages.find(p => p.id === kitId);
    return pkg?.name || kitId;
  };

  // Get kit color by ID  
  const getKitColor = (kitId) => {
    const pkg = packages.find(p => p.id === kitId);
    return pkg?.color || '#6b7280';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Header */}
      <header className="bg-black/40 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/score-creator" className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-400" />
                  Kit Sample Manager
                </h1>
                <p className="text-sm text-gray-400">Upload and organize samples for instrument kits</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="text-2xl font-bold text-purple-400">{stats.total}</div>
            <div className="text-sm text-gray-400">Total Samples</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="text-2xl font-bold text-green-400">{stats.assigned}</div>
            <div className="text-sm text-gray-400">Assigned</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="text-2xl font-bold text-amber-400">{stats.unassigned}</div>
            <div className="text-sm text-gray-400">Unassigned</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="text-2xl font-bold text-blue-400">{packages.length}</div>
            <div className="text-sm text-gray-400">Kits</div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-purple-400" />
            Upload Samples
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Folder Upload */}
            <div className="relative">
              <input
                ref={folderInputRef}
                type="file"
                webkitdirectory=""
                directory=""
                multiple
                onChange={handleFolderUpload}
                className="hidden"
                disabled={uploading}
              />
              <button
                onClick={() => folderInputRef.current?.click()}
                disabled={uploading}
                className="w-full h-32 border-2 border-dashed border-purple-500/50 rounded-xl hover:border-purple-400 hover:bg-purple-500/5 transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50"
              >
                <FolderOpen className="w-8 h-8 text-purple-400" />
                <span className="text-sm font-medium">Upload Folder</span>
                <span className="text-xs text-gray-400">Auto-sorts by folder name</span>
              </button>
            </div>

            {/* File Upload */}
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.wav,.mp3,.ogg,.flac,.aiff,.aif"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full h-32 border-2 border-dashed border-blue-500/50 rounded-xl hover:border-blue-400 hover:bg-blue-500/5 transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50"
              >
                <FileAudio className="w-8 h-8 text-blue-400" />
                <span className="text-sm font-medium">Upload Files</span>
                <span className="text-xs text-gray-400">Select multiple audio files</span>
              </button>
            </div>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>Uploading...</span>
                <span>{uploadProgress.current} / {uploadProgress.total}</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 transition-all"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search samples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Kit Filter */}
          <select
            value={selectedKit}
            onChange={(e) => setSelectedKit(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Kits</option>
            <option value="unassigned">Unassigned</option>
            {packages.map(pkg => (
              <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-500' : 'hover:bg-white/10'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-500' : 'hover:bg-white/10'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Selection Actions */}
          {selectedSamples.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">{selectedSamples.length} selected</span>
              <button
                onClick={() => setShowAssignModal(true)}
                className="px-3 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm flex items-center gap-2"
              >
                <Move className="w-4 h-4" />
                Assign to Kit
              </button>
              <button
                onClick={() => setSelectedSamples([])}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm"
              >
                Clear
              </button>
            </div>
          )}

          <button
            onClick={selectAllVisible}
            className="px-3 py-2 text-sm text-gray-400 hover:text-white"
          >
            Select All
          </button>
        </div>

        {/* Samples Display */}
        {filteredSamples.length === 0 ? (
          <div className="text-center py-16">
            <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No Samples Yet</h3>
            <p className="text-gray-500">Upload a folder or files to get started</p>
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View - Grouped by Kit
          <div className="space-y-6">
            {Object.entries(samplesByKit).map(([kitId, kitSamples]) => (
              <div key={kitId} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => setExpandedKits(prev => ({ ...prev, [kitId]: !prev[kitId] }))}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getKitColor(kitId) }}
                    />
                    <span className="font-medium">{getKitName(kitId)}</span>
                    <span className="text-sm text-gray-400">({kitSamples.length} samples)</span>
                  </div>
                  {expandedKits[kitId] !== false ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </button>

                {expandedKits[kitId] !== false && (
                  <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {kitSamples.map(sample => (
                      <SampleCard
                        key={sample.id}
                        sample={sample}
                        isSelected={selectedSamples.includes(sample.id)}
                        isPlaying={playingSample === sample.id}
                        onToggleSelect={() => toggleSelectSample(sample.id)}
                        onTogglePlay={() => togglePlay(sample)}
                        onDelete={() => handleDelete(sample.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedSamples.length === filteredSamples.length && filteredSamples.length > 0}
                      onChange={() => {
                        if (selectedSamples.length === filteredSamples.length) {
                          setSelectedSamples([]);
                        } else {
                          selectAllVisible();
                        }
                      }}
                      className="rounded"
                    />
                  </th>
                  <th className="p-3 w-10"></th>
                  <th className="p-3">Filename</th>
                  <th className="p-3">Instrument</th>
                  <th className="p-3">Variation</th>
                  <th className="p-3">Kit</th>
                  <th className="p-3 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSamples.map(sample => (
                  <tr key={sample.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedSamples.includes(sample.id)}
                        onChange={() => toggleSelectSample(sample.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => togglePlay(sample)}
                        className={`p-2 rounded-full ${playingSample === sample.id ? 'bg-purple-500' : 'bg-white/10 hover:bg-white/20'}`}
                      >
                        {playingSample === sample.id ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="p-3 font-mono text-sm">{sample.filename}</td>
                    <td className="p-3 text-sm text-gray-400">{sample.instrument_name || '-'}</td>
                    <td className="p-3 text-sm text-gray-400">{sample.variation || '-'}</td>
                    <td className="p-3">
                      <span 
                        className="px-2 py-1 rounded text-xs"
                        style={{ 
                          backgroundColor: `${getKitColor(sample.kit_id)}20`,
                          color: getKitColor(sample.kit_id)
                        }}
                      >
                        {getKitName(sample.kit_id)}
                      </span>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleDelete(sample.id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAssignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-900 rounded-xl w-full max-w-lg border border-white/10 max-h-[80vh] overflow-hidden"
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Assign {selectedSamples.length} Sample(s) to Kit</h3>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {packages.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">
                    No instrument packages created yet.<br />
                    <Link to="/admin/score-creator" className="text-purple-400 hover:underline">
                      Create packages in Score Creator
                    </Link>
                  </p>
                ) : (
                  <div className="space-y-2">
                    {packages.map(pkg => (
                      <button
                        key={pkg.id}
                        onClick={() => handleAssign(pkg.id)}
                        className="w-full p-4 bg-white/5 hover:bg-white/10 rounded-lg flex items-center gap-3 transition-colors"
                      >
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: pkg.color }}
                        />
                        <div className="flex-1 text-left">
                          <div className="font-medium">{pkg.name}</div>
                          <div className="text-sm text-gray-400">
                            {pkg.subInstruments?.length || 0} sub-instruments
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sample Card Component
const SampleCard = ({ sample, isSelected, isPlaying, onToggleSelect, onTogglePlay, onDelete }) => (
  <div 
    className={`relative p-3 rounded-lg border transition-all cursor-pointer ${
      isSelected 
        ? 'bg-purple-500/20 border-purple-500' 
        : 'bg-white/5 border-white/10 hover:border-white/20'
    }`}
    onClick={onToggleSelect}
  >
    {/* Selection indicator */}
    <div className="absolute top-2 right-2">
      {isSelected ? (
        <CheckCircle className="w-5 h-5 text-purple-400" />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-white/30" />
      )}
    </div>

    {/* Play button */}
    <button
      onClick={(e) => { e.stopPropagation(); onTogglePlay(); }}
      className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
        isPlaying ? 'bg-purple-500' : 'bg-white/10 hover:bg-white/20'
      }`}
    >
      {isPlaying ? (
        <Pause className="w-5 h-5" />
      ) : (
        <Play className="w-5 h-5 ml-0.5" />
      )}
    </button>

    {/* Info */}
    <div className="text-center">
      <div className="text-xs font-mono truncate" title={sample.filename}>
        {sample.filename}
      </div>
      {sample.instrument_name && (
        <div className="text-xs text-purple-400 mt-1">{sample.instrument_name}</div>
      )}
      {sample.variation && (
        <div className="text-xs text-gray-500">{sample.variation}</div>
      )}
    </div>

    {/* Delete button */}
    <button
      onClick={(e) => { e.stopPropagation(); onDelete(); }}
      className="absolute bottom-2 right-2 p-1 text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
    >
      <Trash2 className="w-3 h-3" />
    </button>
  </div>
);

export default AdminKitSamples;
